import React, { useState } from "react";
import {
  useUserGroupsQuery,
  useCreateUserGroupMutation,
  useSaveUserGroupMutation,
  useDeleteUserGroupMutation,
  usePermissionsListQuery
} from "@queries/user_group";
import Loader from "@components/Loader";
import "@styles/opencart-admin.css";

export default function UserGroupPage() {
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [selectedId, setSelectedId] = useState(null);

  const emptyForm = {
    name: "",
    accessPermissions: [],
    modifyPermissions: []
  };

  const [form, setForm] = useState(emptyForm);
  const [permissionSearch, setPermissionSearch] = useState("");

  const { data, isLoading, isFetching, isError, error } = useUserGroupsQuery(
    {},
    page,
    pageSize
  );
  const {
    data: permissionsData,
    isLoading: permsLoading,
    isFetching: permsFetching
  } = usePermissionsListQuery();

  const createMutation = useCreateUserGroupMutation();
  const saveMutation = useSaveUserGroupMutation();
  const deleteMutation = useDeleteUserGroupMutation();

  const allPermissions = permissionsData?.permissions || [];

  const filteredPermissions = allPermissions.filter((code) =>
    code.toLowerCase().includes(permissionSearch.toLowerCase())
  );

  const groups = data?.data || [];
  const total = data?.total ?? groups.length;
  const pages = data?.last_page ?? 1;

  const isBusy =
    isLoading ||
    isFetching ||
    permsLoading ||
    permsFetching ||
    createMutation.isPending ||
    saveMutation.isPending;

  function resetForm() {
    setForm(emptyForm);
    setSelectedId(null);
  }

  function handleEdit(row) {
    setSelectedId(row.user_group_id);
    const perm = row.permission || {};
    setForm({
      name: row.name || "",
      accessPermissions: perm.access || [],
      modifyPermissions: perm.modify || []
    });
  }

  function handleToggleAccess(code) {
    setForm((f) => {
      const exists = f.accessPermissions.includes(code);
      return {
        ...f,
        accessPermissions: exists
          ? f.accessPermissions.filter((x) => x !== code)
          : [...f.accessPermissions, code]
      };
    });
  }

  function handleToggleModify(code) {
    setForm((f) => {
      const exists = f.modifyPermissions.includes(code);
      return {
        ...f,
        modifyPermissions: exists
          ? f.modifyPermissions.filter((x) => x !== code)
          : [...f.modifyPermissions, code]
      };
    });
  }

  function handleSave(e) {
    if (e) e.preventDefault();

    const payload = {
      name: form.name,
      permission: {
        access: form.accessPermissions,
        modify: form.modifyPermissions
      }
    };

    if (selectedId) {
      saveMutation.mutate(
        { id: selectedId, payload },
        { onSuccess: () => resetForm() }
      );
    } else {
      createMutation.mutate(payload, { onSuccess: () => resetForm() });
    }
  }

  function handleDelete(id) {
    if (!window.confirm("Delete this user group?")) return;
    deleteMutation.mutate(id, { onSuccess: () => resetForm() });
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching || permsLoading || permsFetching) && <Loader />}

      {/* HEADER */}
      <div className="oc-header">
        <h1>User Groups</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>User Groups</span>
        </nav>
      </div>

      {/* FORM CARD */}
      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>
              {selectedId ? `Edit Group #${selectedId}` : "Create User Group"}
            </strong>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {selectedId && (
              <button
                type="button"
                className="oc-btn ghost"
                onClick={resetForm}
                disabled={isBusy}
              >
                Cancel edit
              </button>
            )}
            <button
              className="oc-btn primary"
              onClick={resetForm}
              disabled={isBusy}
            >
              New Group
            </button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          {/* BASIC INFO */}
          <div className="form-row">
            <div className="form-group">
              <label>
                Name <span style={{ color: "#c00" }}>*</span>
              </label>
              <input
                className="oc-input"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Admin, Manager, Cleaner..."
                required
              />
            </div>
          </div>

          {/* PERMISSIONS */}
          <div style={{ marginTop: 16 }}>
            <div
              className="form-row"
              style={{ alignItems: "flex-start", marginBottom: 8 }}
            >
              <div className="form-group" style={{ flex: 1 }}>
                <label>Search permissions</label>
                <input
                  className="oc-input"
                  value={permissionSearch}
                  onChange={(e) => setPermissionSearch(e.target.value)}
                  placeholder="Type to filter permissions..."
                />
              </div>
            </div>

            <div
              className="form-row"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16
              }}
            >
              {/* ACCESS PERMISSIONS */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Access Permissions</label>
                <div
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    height: 260,
                    overflow: "auto",
                    padding: 8,
                    fontSize: 13,
                    background: "#fff"
                  }}
                >
                  {filteredPermissions.length === 0 && (
                    <div style={{ padding: 4, color: "#888" }}>
                      No permissions found.
                    </div>
                  )}
                  {filteredPermissions.map((code) => (
                    <label
                      key={`access-${code}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "2px 0"
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.accessPermissions.includes(code)}
                        onChange={() => handleToggleAccess(code)}
                      />
                      <span>{code}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* MODIFY PERMISSIONS */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Modify Permissions</label>
                <div
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    height: 260,
                    overflow: "auto",
                    padding: 8,
                    fontSize: 13,
                    background: "#fff"
                  }}
                >
                  {filteredPermissions.length === 0 && (
                    <div style={{ padding: 4, color: "#888" }}>
                      No permissions found.
                    </div>
                  )}
                  {filteredPermissions.map((code) => (
                    <label
                      key={`modify-${code}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "2px 0"
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.modifyPermissions.includes(code)}
                        onChange={() => handleToggleModify(code)}
                      />
                      <span>{code}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div
            className="oc-modal-footer"
            style={{ padding: 0, marginTop: 16, borderTop: "none" }}
          >
            {selectedId && (
              <button
                type="button"
                className="oc-btn danger"
                onClick={() => handleDelete(selectedId)}
                disabled={isBusy}
              >
                Delete
              </button>
            )}
            <button
              type="submit"
              className="oc-btn primary"
              disabled={isBusy}
            >
              {isBusy
                ? "Saving..."
                : selectedId
                ? "Update Group"
                : "Create Group"}
            </button>
          </div>
        </form>
      </div>

      {/* LIST CARD */}
      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>User Groups</strong>
          </div>
          <div style={{ fontSize: 12, color: "#666" }}>
            Total groups: {total}
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            Error: {error?.message || "Unknown error"}
          </div>
        )}

        <table className="oc-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>ID</th>
              <th>Name</th>
              <th style={{ width: 120 }}>Access count</th>
              <th style={{ width: 120 }}>Modify count</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: 24 }}>
                  No user groups found.
                </td>
              </tr>
            )}
            {groups.map((row) => {
              const perm = row.permission || {};
              const accessCount = (perm.access || []).length;
              const modifyCount = (perm.modify || []).length;

              const isSelected = row.user_group_id === selectedId;

              return (
                <tr
                  key={row.user_group_id}
                  className={isSelected ? "oc-row-selected" : ""}
                >
                  <td>{row.user_group_id}</td>
                  <td>{row.name}</td>
                  <td>{accessCount}</td>
                  <td>{modifyCount}</td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="oc-btn blue"
                        type="button"
                        onClick={() => handleEdit(row)}
                        disabled={isBusy}
                      >
                        edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINATION CARD */}
      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div>
          Page <strong>{page}</strong> of <strong>{pages}</strong> ({total}{" "}
          groups)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹ Prev
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage((p) => (p >= pages ? p : p + 1))}
          >
            Next ›
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

